import React from "react";
import { Link } from "react-router-dom";
import { useReveal } from "./useReveal";
import "./HelpHubPage.css";

/**
 * THE OPPORTUNITY HUB — "I need help. What's my next step?"
 * Phase 1 of the Community Opportunity Hub: an honest, curated guide that
 * points neighbors to real, established NYC resources — plus the things
 * Everything Eco can do directly. We are the guide, not the gatekeeper.
 */

type Resource = { name: string; what: string; url: string };
type Cat = { icon: string; title: string; sub: string; direct?: string; resources: Resource[] };

const CATS: Cat[] = [
  {
    icon: "💼", title: "Find a job",
    sub: "Real help with job searches, training and placement.",
    resources: [
      { name: "Workforce1 (NYC SBS)", what: "The city's free career centers — coaching, training, and direct connections to jobs.", url: "https://www.nyc.gov/site/sbs/index.page" },
      { name: "NYC 311", what: "Ask for employment programs near you — they'll route you to the right one.", url: "https://portal.311.nyc.gov/" },
    ],
  },
  {
    icon: "🚀", title: "Start a business",
    sub: "From idea to open — free help exists for every step.",
    direct: "Building something? We've been where you are — reach out and we'll share everything we're learning as a startup ourselves.",
    resources: [
      { name: "NYC Small Business Services", what: "Free courses, help with permits and plans, and small-business financing programs.", url: "https://www.nyc.gov/site/sbs/index.page" },
      { name: "SCORE NYC", what: "Free one-on-one mentoring from experienced business owners.", url: "https://www.score.org/" },
      { name: "Public libraries (NYPL / BPL)", what: "Free business databases, workshops and meeting space — seriously underrated.", url: "https://www.nypl.org/" },
    ],
  },
  {
    icon: "🤖", title: "Learn computers & AI",
    sub: "Technology should open doors, not close them.",
    direct: "We refurbish donated laptops for students and families who need them — ask us.",
    resources: [
      { name: "NYPL TechConnect", what: "Free computer classes at library branches — from total beginner to coding.", url: "https://www.nypl.org/" },
      { name: "Brooklyn Public Library", what: "Free tech training, resume labs and wifi hotspot lending.", url: "https://www.bklynlibrary.org/" },
    ],
  },
  {
    icon: "🎓", title: "Finish school / GED",
    sub: "It's never too late — and it's free.",
    resources: [
      { name: "NYC Adult Education", what: "Free high-school equivalency (GED/TASC) classes across the city.", url: "https://www.schools.nyc.gov/" },
      { name: "CUNY", what: "City University programs, including bridge programs for returning students.", url: "https://www.cuny.edu/" },
    ],
  },
  {
    icon: "📄", title: "Resume & interview help",
    sub: "Get your story on paper and practice telling it.",
    resources: [
      { name: "Library resume labs", what: "Librarians will literally sit with you and build your resume, free.", url: "https://www.nypl.org/" },
      { name: "Workforce1 coaching", what: "Mock interviews and application help from career advisors.", url: "https://www.nyc.gov/site/sbs/index.page" },
    ],
  },
  {
    icon: "🍎", title: "Food & essentials",
    sub: "No shame, ever. Everyone needs a hand sometimes.",
    direct: "Our clothing and household donations go to neighbors first — tell us what you need.",
    resources: [
      { name: "NYC 311 — Food help", what: "Find food pantries and free meals near you, today.", url: "https://portal.311.nyc.gov/" },
    ],
  },
  {
    icon: "🏠", title: "Housing questions",
    sub: "Tenant rights, assistance programs, emergency help.",
    resources: [
      { name: "NYC 311 — Housing", what: "The front door to every city housing program and tenant helpline.", url: "https://portal.311.nyc.gov/" },
    ],
  },
  {
    icon: "🤝", title: "Volunteer & give back",
    sub: "The fastest way to feel connected to your city.",
    direct: "Join an Everything Eco cleanup or drive — message us and you're in.",
    resources: [
      { name: "NYC Service", what: "The city's official volunteer match — thousands of opportunities.", url: "https://www.nycservice.org/" },
    ],
  },
];

const HelpHubPage: React.FC = () => {
  useReveal();
  return (
    <div className="hh-page">
      {/* ── Hero ── */}
      <section className="hh-hero">
        <div className="ee-container hh-hero-inner">
          <div className="hh-kicker">🧭 THE OPPORTUNITY HUB</div>
          <h1>Don't know your next step?<br /><span className="hh-grad">Start here.</span></h1>
          <p className="hh-sub">
            Everything Eco started because our neighborhood deserved more ways up. This page is our
            guide to real, free help across NYC — the stuff we do ourselves, and the trusted places
            we'd send our own family.
          </p>
        </div>
      </section>

      {/* ── Honesty card ── */}
      <section className="ee-section hh-honest">
        <div className="ee-container hh-honest-card">
          <span>💚</span>
          <p>
            <strong>Real talk:</strong> we're a small, growing team — we can't do everything yet. So we do
            two things honestly: what we <em>can</em> do directly, we do. For everything else, we point you
            to established, legit resources — most of them free and run by the city or public libraries.
            No runaround. <span className="hh-phase">PHASE 1 · THIS PAGE GROWS AS OUR PARTNERSHIPS DO</span>
          </p>
        </div>
      </section>

      {/* ── Categories ── */}
      <section className="ee-section hh-cats">
        <div className="ee-container">
          <div className="hh-grid">
            {CATS.map((c) => (
              <div className="hh-card" key={c.title}>
                <div className="hh-card-head">
                  <span className="hh-ico">{c.icon}</span>
                  <div>
                    <h3>{c.title}</h3>
                    <p>{c.sub}</p>
                  </div>
                </div>
                {c.direct && (
                  <div className="hh-direct">
                    <span>WE HELP DIRECTLY</span> {c.direct}
                  </div>
                )}
                <ul>
                  {c.resources.map((r) => (
                    <li key={r.name}>
                      <a href={r.url} target="_blank" rel="noreferrer">{r.name} ↗</a>
                      <p>{r.what}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="ee-section hh-cta">
        <div className="ee-container" style={{ textAlign: "center" }}>
          <h2 className="ee-section-title">Not sure which door is yours?</h2>
          <p className="hh-cta-sub">
            Tap Earth Jr. in the corner and just tell him what's going on — or write to us directly.
            A real person reads everything.
          </p>
          <div className="hh-cta-btns">
            <Link to="/contact" className="ee-btn ee-btn-green">✉️ Talk to us</Link>
            <Link to="/mission" className="ee-btn hh-btn-outline">Why we do this →</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpHubPage;
